import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { PhotoPicker } from "@/components/PhotoPicker";

export function ChefLessonComposer() {
  const [topic, setTopic] = useState("");
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState([]);
  const load = () => api.get("/teams/lessons").then((r) => setSent(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      let data;
      if (photo) {
        const form = new FormData();
        form.append("file", photo);
        form.append("topic", topic);
        ({ data } = await api.post("/teams/photo-lessons", form));
      } else {
        ({ data } = await api.post("/teams/lessons", { topic }));
      }
      toast.success(`« ${data.title} » envoyée à ${data.sent_to} personne(s)`);
      setTopic("");
      setPhoto(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="bg-white rounded-2xl border-2 border-slate-200 p-4 space-y-3 rise" data-testid="chef-lesson-composer">
      <p className="text-xs font-bold uppercase text-slate-500">Envoyer une leçon à l'équipe</p>
      <form onSubmit={submit} className="space-y-3">
        <PhotoPicker photo={photo} onChange={setPhoto} />
        <Textarea
          value={topic}
          onChange={(e) => setTopic(e.target.value)}
          maxLength={200}
          placeholder={photo ? "Précision facultative (ex : montage du coffrage)" : "Ex : coulage de la dalle du 2e étage, consignes de sécurité sur la nacelle…"}
          className="min-h-[90px] text-base"
          data-testid="chef-lesson-topic-input"
        />
        <button
          type="submit"
          disabled={busy || (!photo && topic.trim().length < 3)}
          data-testid="chef-lesson-send-btn"
          className="w-full h-12 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-bold flex items-center justify-center gap-2 transition-colors disabled:opacity-40"
        >
          {busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          {busy ? "Création de la leçon…" : "Créer et envoyer"}
        </button>
      </form>
      {sent.length > 0 && (
        <div className="space-y-2" data-testid="chef-lessons-sent">
          {sent.map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm" data-testid={`chef-lesson-sent-${l.id}`}>
              <span className="font-semibold truncate">{l.title}</span>
              <span className="flex items-center gap-1 font-bold text-emerald-700 shrink-0"><CheckCircle2 className="w-4 h-4" />{l.done}/{l.total}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export function AssignedLessons() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/lessons/assigned").then((r) => setItems(r.data)).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <section className="space-y-2 rise" data-testid="home-assigned-lessons">
      <p className="text-xs font-bold uppercase tracking-widest text-orange-600">Leçons du chef</p>
      {items.map((l) => (
        <Link key={l.id} to={`/lecon/${l.id}`} data-testid={`home-assigned-${l.id}`} className="flex items-center gap-3 rounded-2xl bg-orange-500 text-white p-4 hover:bg-orange-600 transition-colors">
          <Send className="w-6 h-6 shrink-0" />
          <span className="flex-1 font-display text-xl font-bold uppercase leading-tight">{l.title}</span>
          <span className="text-xs font-bold uppercase bg-white/20 rounded-full px-2 py-1">À faire</span>
        </Link>
      ))}
    </section>
  );
}
